import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ApiError, NetworkError } from "../api/client";
import { getMembers, type MembersPageResponse, type MembershipCategory } from "../api/members";
import { useAuth } from "../auth/AuthContext";
import { MemberFilters } from "../components/members/MemberFilters";
import { MemberTable } from "../components/members/MemberTable";
import { StatusBanner } from "../components/StatusBanner";
import { useOnlineStatus } from "../hooks/useOnlineStatus";

const PAGE_SIZE = 20;

type MembersView =
  | { type: "loading" }
  | { type: "ready"; data: MembersPageResponse }
  | { type: "empty" }
  | { type: "offline" }
  | { type: "forbidden" }
  | { type: "error"; message: string };

function resolveInitialSearch(state: unknown): string {
  if (state && typeof state === "object" && "search" in state) {
    const candidate = (state as { search?: unknown }).search;
    if (typeof candidate === "string") {
      return candidate;
    }
  }
  return "";
}

function mapFetchErrorToView(error: unknown, expireSession: () => void): MembersView | null {
  if (error instanceof ApiError && error.status === 401) {
    expireSession();
    return null;
  }
  if (error instanceof ApiError && error.status === 403) {
    return { type: "forbidden" };
  }
  if (error instanceof NetworkError) {
    return { type: "offline" };
  }
  return { type: "error", message: "Não foi possível carregar os associados. Tente novamente." };
}

function MembersEmptyState({
  hasFilter,
  onReset,
}: {
  hasFilter: boolean;
  onReset: () => void;
}) {
  if (hasFilter) {
    return (
      <section className="members-empty" aria-live="polite">
        <h2>Nenhum resultado para este filtro</h2>
        <p>Ajuste a pesquisa ou a categoria selecionada.</p>
        <button type="button" className="btn btn--secondary" onClick={onReset}>
          Limpar filtros
        </button>
      </section>
    );
  }

  return (
    <section className="members-empty" aria-live="polite">
      <h2>Nenhum associado cadastrado</h2>
      <p>Ainda não há associados registrados no sistema.</p>
    </section>
  );
}

function MembersPagination({
  page,
  pageSize,
  total,
  onPageChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (newPage: number) => void;
}) {
  const hasPrevious = page > 1;
  const hasNext = page * pageSize < total;

  return (
    <nav className="members-pagination" aria-label="Paginação de associados">
      <button type="button" onClick={() => onPageChange(page - 1)} disabled={!hasPrevious}>
        Anterior
      </button>
      <span>Página {page}</span>
      <button type="button" onClick={() => onPageChange(page + 1)} disabled={!hasNext}>
        Próxima
      </button>
    </nav>
  );
}

function MembersReadyView({
  data,
  onPageChange,
}: {
  data: MembersPageResponse;
  onPageChange: (newPage: number) => void;
}) {
  const resultText = data.total === 1 ? "1 associado encontrado" : `${data.total} associados encontrados`;
  return (
    <>
      <p className="members-page__result-count" aria-live="polite">
        {resultText}
      </p>
      <MemberTable members={data.items} />
      <MembersPagination
        page={data.page}
        pageSize={data.page_size}
        total={data.total}
        onPageChange={onPageChange}
      />
    </>
  );
}

function MembersViewContainer({
  view,
  hasFilter,
  onResetFilters,
  onPageChange,
}: {
  view: MembersView;
  hasFilter: boolean;
  onResetFilters: () => void;
  onPageChange: (page: number) => void;
}) {
  if (view.type === "loading") {
    return <p className="members-state" role="status" aria-live="polite">Carregando associados…</p>;
  }
  if (view.type === "offline") {
    return <StatusBanner tone="warning">Sem conexão. Verifique a internet e tente novamente.</StatusBanner>;
  }
  if (view.type === "forbidden") {
    return <StatusBanner tone="error">Seu acesso não possui permissão para consultar associados.</StatusBanner>;
  }
  if (view.type === "error") {
    return <StatusBanner tone="error">{view.message}</StatusBanner>;
  }
  if (view.type === "empty") {
    return <MembersEmptyState hasFilter={hasFilter} onReset={onResetFilters} />;
  }
  return <MembersReadyView data={view.data} onPageChange={onPageChange} />;
}

export function MembersPage() {
  const { state, expireSession } = useAuth();
  const isOnline = useOnlineStatus();
  const accessToken = state.status === "authenticated" ? state.accessToken : null;
  const location = useLocation();
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState(() => resolveInitialSearch(location.state));
  const [search, setSearch] = useState(() => resolveInitialSearch(location.state).trim());
  const [category, setCategory] = useState<MembershipCategory | "">("");
  const [page, setPage] = useState(1);
  const [view, setView] = useState<MembersView>({ type: "loading" });

  useEffect(() => {
    const term = resolveInitialSearch(location.state);
    if (term) {
      setSearchInput(term);
      setSearch(term.trim());
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [category]);

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;

    if (!isOnline) {
      setView({ type: "offline" });
      return;
    }

    setView({ type: "loading" });
    void getMembers(accessToken, { search: search || undefined, category: category || undefined, page, pageSize: PAGE_SIZE })
      .then((data) => {
        if (cancelled) return;
        setView(data.items.length === 0 ? { type: "empty" } : { type: "ready", data });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const nextView = mapFetchErrorToView(error, expireSession);
        if (nextView) setView(nextView);
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, category, expireSession, isOnline, page, search]);

  if (state.status !== "authenticated") return null;

  const hasFilter = Boolean(search || category);
  const canCreate = Boolean(state.sessionContext?.capabilities.create_members);

  return (
    <main className="members-page">
      <header className="members-page__intro">
        <p className="members-page__eyebrow">Secretaria e DAF</p>
        <h1>Associados</h1>
        <p>Consulta institucional de cadastros, vínculos e histórico de categoria.</p>
        {canCreate && (
          <Link className="btn btn--primary members-page__create" to="/associados/novo">
            Novo associado
          </Link>
        )}
      </header>

      <MemberFilters
        search={searchInput}
        category={category}
        onSearchChange={setSearchInput}
        onCategoryChange={setCategory}
      />

      <MembersViewContainer
        view={view}
        hasFilter={hasFilter}
        onResetFilters={() => {
          setSearchInput("");
          setCategory("");
        }}
        onPageChange={setPage}
      />
    </main>
  );
}
